import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { resetALL } from "Redux/actions";

const INITIAL_STATE = {
  selectedChat: {
    openModal: false,
    loading: false,
    channel: null,
    items: [],
    nextToken: null,
    error: null,
  },
};

const route = "modules/chat/views/chatTypes/team/redux/listSlice";

const querySearchChatMessage = async (chatChannelId, limit, nextToken) => {
  return clientQuery(
    `
      query searchChatMessage ($chatChannelId: String!, $limit: Int, $nextToken: String) {
        data: searchChatMessage (chatChannelId: $chatChannelId, limit: $limit, nextToken: $nextToken) {
          items {
            id
            chatChannelId
            transmitterEntityNameId
            receiverEntityNameId
            message
            status
            createdOn
            sendedOn
            receivedOn
            readOn
            updatedOn
          }
          nextToken
        }
      }
    `,
    {
      chatChannelId,
      limit,
      nextToken,
    },
    endpoints.GRAPHQL_GENERAL_V2
  );
};

const mutationUpdateChatMessageStatus = async (ids, status) => {
  return clientMutation(
    `
      mutation updateChatMessageStatus($ids: [String]!, $status: enumChatMessageStatus!){
        data: updateChatMessageStatus(ids: $ids, status: $status)
      }
    `,
    { ids, status },
    endpoints.GRAPHQL_GENERAL_V2
  );
};

export const updateChatChannelMessagesStatus = createAsyncThunk(
  "chat/memberList/updateMessages",
  async ({ chatChannelId, fromMessageStatus, toMessageStatus, authId }, { rejectWithValue, dispatch }) => {
    const limit = 30;
    try {
      const firstPage = await querySearchChatMessage(chatChannelId, limit, null);
      let allMessages = [...firstPage?.data?.items]
      let nextToken = firstPage?.data?.nextToken;
      while (nextToken !== null) {
        const nextPage = await querySearchChatMessage(chatChannelId, limit, nextToken);
        nextToken = nextPage?.data?.nextToken;
        allMessages = [...allMessages, ...nextPage?.data?.items];
      }

      if (allMessages?.length) {
        const allMatchingIds = allMessages.filter(message => message?.receiverEntityNameId === authId && message?.status === fromMessageStatus).map((message) => message.id);
        if (allMatchingIds?.length) {
          await mutationUpdateChatMessageStatus(allMatchingIds, toMessageStatus);
        }
      }
    } catch (error) {
      ExceptionManager(error, route, "updateChatChannelMessagesStatus");
      return rejectWithValue(error);
    }
  }
);

export const openChatThread = createAsyncThunk(
  "chat/thread/open",
  async ({ chatId, authId }, { rejectWithValue, dispatch }) => {
    try {
      const result = await clientQuery(
        `
        query getChatChannel($id: String!) {
          data: getChatChannel (id: $id) {
            id
            isGroup
            name
            users
            avatar
          }
        }
      `,
        { id: chatId },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (result?.data) {
        dispatch(fetchChatMessages({ chatId, authId, limit: 10, nextToken: null }));
      }

      return result?.data;
    } catch (error) {
      ExceptionManager(error, route, "openChatThread");
      return rejectWithValue(error);
    }
  }
);

export const fetchChatMessages = createAsyncThunk(
  "chat/thread/messages",
  async ({ chatId, limit, nextToken = "", ascending = false, createdOn = null }, { rejectWithValue }) => {
    try {
      const result = await clientQuery(
        `
        query searchChatMessage ($chatChannelId: String!, $limit: Int, $nextToken: String, $ascending: Boolean, $createdOn: AWSDateRangeInput) {
          data: searchChatMessage (chatChannelId: $chatChannelId, limit: $limit, nextToken: $nextToken, ascending: $ascending, createdOn: $createdOn) {
            items {
              id
              chatChannelId
              transmitterEntityNameId
              receiverEntityNameId
              message
              status
              createdOn
              sendedOn
              receivedOn
              readOn
              updatedOn
            }
            nextToken
          }
        }
      `,
        {
          chatChannelId: chatId,
          limit,
          nextToken,
          ascending,
          createdOn,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return result?.data || { items: [], nextToken: null };
    } catch (error) {
      ExceptionManager(error, route, "fetchChatMessages");
      return rejectWithValue(error);
    }
  }
);

export const sendChatMessage = createAsyncThunk(
  "chat/thread/send",
  async ({ chatId, transmitterEntityNameId, receiverEntityNameId, message, clientMessageId,
    nextToken
  }, { rejectWithValue }) => {
    try {
      const result = await clientMutation(
        `
        mutation createChatMessage ($chatChannelId: String!, $transmitterEntityNameId: String!, $receiverEntityNameId: String!, $message: String!) {
          data: createChatMessage (chatChannelId: $chatChannelId, transmitterEntityNameId: $transmitterEntityNameId, receiverEntityNameId: $receiverEntityNameId, message: $message) {
            id
            chatChannelId
            transmitterEntityNameId
            receiverEntityNameId
            message
            status
            createdOn
            sendedOn
            receivedOn
            readOn
            updatedOn
          }
        }
      `,
        {
          chatChannelId: chatId,
          transmitterEntityNameId,
          receiverEntityNameId,
          message,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return {
        message: {...result?.data, clientMessageId},
      };
    } catch (error) {
      ExceptionManager(error, route, "sendChatMessage");
      return rejectWithValue(error);
    }
  }
);

export const updateChatMessageStatus = createAsyncThunk(
  "chat/thread/updateStatus",
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      const result = await clientMutation(
        `
        mutation updateChatMessageStatus($ids: [String]!, $status: enumChatMessageStatus!){
          data: updateChatMessageStatus(ids: $ids, status: $status)
        }
      `,
        { ids, status },
        endpoints.GRAPHQL_GENERAL_V2
      );
      return result?.data;
    } catch (error) {
      ExceptionManager(error, route, "updateChatMessageStatus");
      return rejectWithValue(error);
    }
  }
);

export const setChatUserWriting = createAsyncThunk(
  "chat/thread/writing",
  async ({ chatId, userId }, { rejectWithValue }) => {
    try {
      await clientMutation(
        `
        mutation statusChatUserChannel($channelId: String!, $userId: String!, $status: enumChatUserStatus!){
          statusChatUserChannel(
            channelId: $channelId
            userId: $userId
            status: $status
          )
        }
      `,
        {
          channelId: chatId,
          userId,
          status: "Writing",
        },
        endpoints.GRAPHQL_GENERAL_V2
      );
      return null;
    } catch (error) {
      ExceptionManager(error, route, "setChatUserWriting");
      return rejectWithValue(error);
    }
  }
);

const teamChatListRedux = createSlice({
  name: "chat/memberList",
  initialState: INITIAL_STATE,
  reducers: {
    reset(state, action) {
      return action.payload
        ? { ...state, selectedChat: { ...state.selectedChat, ...action.payload } }
        : { ...INITIAL_STATE };
    },
    chatMessageUpdateSNS(state, action) {

      if (!state.selectedChat.openModal || !action.payload) {
        return;
      }

      try {
        const channelId = state.selectedChat.channel?.id;
        if (!channelId || channelId !== action.payload.chatChannelId) {
          return;
        }

        const currentItems = Array.isArray(state.selectedChat.items) ? state.selectedChat.items : [];

        // Normalize createdOn to ISO string to prevent moment.js deprecation warnings
        const newMessage = {
          ...action.payload,
          createdOn: action.payload.createdOn instanceof Date
            ? action.payload.createdOn.toISOString()
            : action.payload.createdOn
        };

        const existingIndex = currentItems.findIndex(item => item.id === newMessage.id);

        if (existingIndex > -1) {
          // Update existing message
          state.selectedChat.items[existingIndex] = {
            ...currentItems[existingIndex],
            ...newMessage
          };
        } else {
          // Add new message
          state.selectedChat.items = [newMessage, ...currentItems];
        }
      } catch (error) {
        console.error('Error in chatMessageUpdateSNS:', error);
      }
    },
    close(state) {
      state.selectedChat = {
        ...state.selectedChat,
        openModal: false,
        items: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INITIAL_STATE);
    builder.addCase(openChatThread.pending, (state) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: true,
        openModal: true,
        items: [],
        nextToken: null,
        error: null,
      };
    });
    builder.addCase(openChatThread.fulfilled, (state, action) => {
      state.selectedChat = {
        ...state.selectedChat,
        channel: { ...action.payload, type: "team" },
        error: null,
      };
    });
    builder.addCase(openChatThread.rejected, (state, action) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        error: action.payload,
      };
    });

    builder.addCase(fetchChatMessages.pending, (state) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: true,
        error: null,
      };
    });
    builder.addCase(fetchChatMessages.fulfilled, (state, action) => {
      // Normalize createdOn fields to ISO strings to prevent moment.js deprecation warnings
      const normalizedItems = (action.payload.items || []).map(item => ({
        ...item,
        createdOn: item.createdOn instanceof Date
          ? item.createdOn.toISOString()
          : item.createdOn
      }));

      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        nextToken: action.payload.nextToken,
        items: Array.from(
          new Map([...(state.selectedChat.items || []), ...normalizedItems]
            .map((mes) => [mes.id, mes])).values()
        ),
        error: null,
      };
    });
    builder.addCase(fetchChatMessages.rejected, (state, action) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        error: action.payload,
      };
    });

    builder.addCase(sendChatMessage.pending, (state, action) => {
      const { chatId, transmitterEntityNameId, receiverEntityNameId, message, clientMessageId } = action.meta.arg;
      const optimisticMessage = {
        id: clientMessageId || `temp-${Date.now()}`,
        chatChannelId: chatId,
        transmitterEntityNameId,
        receiverEntityNameId,
        message,
        status: "Sent",
        createdOn: new Date().toISOString(),
        optimistic: true,
      };

      state.selectedChat = {
        ...state.selectedChat,
        error: null,
        items: [...(state.selectedChat.items || []), optimisticMessage],
      };
    });
    builder.addCase(sendChatMessage.fulfilled, (state, action) => {
      const { clientMessageId } = action.meta.arg;
      let items = [...(state.selectedChat.items || [])];
      const { clientMessageId: _clientMessageId, ...serverMessage } = action.payload.message || {};
      const index = clientMessageId ? items.findIndex((item) => item.id === clientMessageId) : -1;

      if (index > -1) {
        items[index] = { ...items[index], ...serverMessage, optimistic: false };
      } else {
        items = [...items, serverMessage];
      }

      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        items,
        error: null,
      };
    });
    builder.addCase(sendChatMessage.rejected, (state, action) => {
      const { clientMessageId } = action.meta.arg || {};
      let items = [...(state.selectedChat.items || [])];
      if (clientMessageId) {
        items = items.filter((item) => item.id !== clientMessageId);
      }

      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        items,
        error: action.payload,
      };
    });

    builder.addCase(updateChatMessageStatus.pending, (state) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: true,
        error: null,
      };
    });
    builder.addCase(updateChatMessageStatus.fulfilled, (state) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
      };
    });
    builder.addCase(updateChatMessageStatus.rejected, (state, action) => {
      state.selectedChat = {
        ...state.selectedChat,
        loading: false,
        error: action.payload,
      };
    });
  },
});

export const { reset, chatMessageUpdateSNS, close } = teamChatListRedux.actions;

export default teamChatListRedux.reducer;
